import React from 'react';
import { Button, Result, Space, Typography } from 'antd';
import { CreditCard, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHasAdminAccess } from '../hooks/useHasAccess';
import { ROUTES } from '../constants/routes';
import { BRAND_PRIMARY } from '../constants/brand';
import type { FeatureKey } from '../features/registry';

interface Props {
  feature: FeatureKey;
}

interface FeaturePlanInfo {
  /**
   * Required plan tier, when the feature is plan-gated. Omitted for
   * entitlement-gated chargeable extras (sdr_agent / mkt) which are add-ons,
   * not a plan tier — those show only the description + "Ver planos" CTA.
   */
  requiredPlan?: string;
  /** i18n key suffix under `features.locked.descriptions` */
  descriptionKey: string;
}

/**
 * Maps a premium FeatureKey to its required plan tier and the i18n key for
 * the long-form description shown on the paywall page. Features not in
 * this map fall back to a generic "not included in your plan" message.
 *
 * Keep this aligned with backend plan entitlement metadata — when a new
 * premium feature is added to the registry, add a row here so the user
 * sees concrete copy ("Win/Loss faz parte do plano Pro...") instead of
 * the generic fallback.
 */
const FEATURE_REQUIRED_PLAN: Partial<Record<FeatureKey, FeaturePlanInfo>> = {
  winloss: { requiredPlan: 'Pro', descriptionKey: 'winloss' },
  roi_calculator: { requiredPlan: 'Pro', descriptionKey: 'roi_calculator' },
  agent: { requiredPlan: 'Business', descriptionKey: 'agent' },
  sdr: { requiredPlan: 'Pro', descriptionKey: 'sdr' },
  // Entitlement-gated chargeable extras (opt-out — D13): add-ons, no plan tier.
  sdr_agent: { descriptionKey: 'sdr_agent' },
  mkt: { descriptionKey: 'mkt' },
  crm_integration: { requiredPlan: 'Pro', descriptionKey: 'crm_integration' },
  nps: { requiredPlan: 'Pro', descriptionKey: 'nps' },
  scoring: { requiredPlan: 'Pro', descriptionKey: 'scoring' },
};

const PRIMARY_COLOR = BRAND_PRIMARY;

/**
 * Full-page paywall shown when a tenant tries to access a feature that's
 * not included in their current plan. Replaces the legacy silent
 * `<Navigate to="/" />` redirect — managers get a direct CTA to billing,
 * operational users get a clear "ask your admin" message via the
 * dashboard fallback.
 */
const FeatureLockedPage: React.FC<Props> = ({ feature }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewBilling = useHasAdminAccess();

  const planInfo = FEATURE_REQUIRED_PLAN[feature];
  const description = planInfo
    ? t(`features.locked.descriptions.${planInfo.descriptionKey}`)
    : t(
        'features.locked.descriptions.fallback',
        'Esta funcionalidade não está incluída no seu plano atual.',
      );

  return (
    <div
      style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <Result
        icon={
          <div
            style={{
              width: 96,
              height: 96,
              margin: '0 auto',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${PRIMARY_COLOR}1A`,
              boxShadow: '0 4px 16px rgba(79, 70, 229, 0.08)',
            }}
          >
            <Lock size={64} color={PRIMARY_COLOR} strokeWidth={1.75} />
          </div>
        }
        title={
          <Typography.Title level={3} style={{ marginBottom: 8, fontSize: 22 }}>
            {t('features.locked.title', 'Recurso disponível em outro plano')}
          </Typography.Title>
        }
        subTitle={
          <div style={{ maxWidth: 520, margin: '0 auto' }}>
            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: 15, marginBottom: 12 }}
            >
              {description}
            </Typography.Paragraph>
            {planInfo?.requiredPlan && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('features.locked.requiresPlan_short', 'Requer plano {{plan}} ou superior', {
                  plan: planInfo.requiredPlan,
                })}
              </Typography.Text>
            )}
          </div>
        }
        extra={
          <Space size={12} wrap style={{ justifyContent: 'center', marginTop: 8 }}>
            {canViewBilling && (
              <Button
                type="primary"
                size="large"
                icon={<CreditCard size={16} />}
                onClick={() => navigate(ROUTES.ADMIN_BILLING)}
                style={{
                  background: PRIMARY_COLOR,
                  borderColor: PRIMARY_COLOR,
                  fontWeight: 500,
                }}
              >
                {t('features.locked.viewPlans', 'Ver planos')}
              </Button>
            )}
            <Button size="large" onClick={() => navigate('/')}>
              {t('features.locked.backToDashboard', 'Voltar para o dashboard')}
            </Button>
          </Space>
        }
      />
    </div>
  );
};

export default FeatureLockedPage;
